import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SlotImage from '@/components/base/SlotImage';
import { supabase } from '@/lib/supabase';
import { recordDonation, sendDonationEmails } from '@/lib/api';
import { usePureheartSettings } from '@/hooks/usePureheartSettings';
import type { PureHeartStat, PureHeartProgram } from '@/lib/types';

const FALLBACK_PROGRAMS: PureHeartProgram[] = [
  {
    title: 'Education',
    icon: 'ri-graduation-cap-line',
    items: [
      { bold: 'Scholarship Programs:', text: 'Providing financial support for underprivileged students.' },
      { bold: 'School Construction:', text: 'Building schools in rural areas to ensure children have access to quality education.' },
      { bold: 'Teacher Training:', text: 'Equipping educators with the skills needed to inspire and educate.' },
    ],
  },
  {
    title: 'Health',
    icon: 'ri-hospital-line',
    items: [
      { bold: 'Mobile Clinics:', text: 'Bringing essential medical services to remote areas.' },
      { bold: 'Nutrition Programs:', text: 'Educating families on healthy eating and providing food resources.' },
      { bold: 'HIV/AIDS Awareness:', text: 'Promoting education and prevention strategies.' },
    ],
  },
  {
    title: 'Volunteer',
    icon: 'ri-hand-heart-line',
    items: [
      { bold: 'Join Us:', text: 'Whether you have time, skills, or resources, your contribution is invaluable.' },
      { bold: 'Partner With Us:', text: 'We welcome partnerships with individuals, businesses, and organizations who share our vision of a better Africa.' },
      { bold: 'Together:', text: 'We can create impactful change across communities.' },
    ],
  },
  {
    title: 'Community Development',
    icon: 'ri-community-line',
    items: [
      { bold: 'Water Projects:', text: 'Providing clean, safe drinking water to communities in need.' },
      { bold: 'Housing Support:', text: 'Building and improving homes for vulnerable families.' },
      { bold: 'Agricultural Programs:', text: 'Teaching sustainable farming practices to ensure food security.' },
    ],
  },
];

function AnimatedCounter({ target, suffix, prefix }: { target: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animated.current) {
        animated.current = true;
        const duration = 2000;
        const start = performance.now();
        const update = (now: number) => {
          const t = Math.min((now - start) / duration, 1);
          const ease = 1 - Math.pow(1 - t, 3);
          setCount(Math.round(ease * target));
          if (t < 1) requestAnimationFrame(update);
        };
        requestAnimationFrame(update);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-4xl font-heading font-bold text-primary-600">
      {prefix}{count.toLocaleString()}{suffix}
    </div>
  );
}

export default function PureheartPage() {
  const [donationAmount, setDonationAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const { data: ph } = usePureheartSettings();
  const stats: PureHeartStat[] = ph?.stats?.length ? ph.stats : [
    { label: 'Success Stories', value: 10, prefix: '0', suffix: '' },
    { label: 'Children Helped', value: 150, prefix: '', suffix: '' },
    { label: 'Non-Profit Communities', value: 25, prefix: '', suffix: '+' },
    { label: 'Working Volunteers', value: 250, prefix: '', suffix: '+' },
  ];
  const programs: PureHeartProgram[] = ph?.programs?.length ? ph.programs : FALLBACK_PROGRAMS;
  const mission = ph?.mission || 'To follow the model of our Lord and Savior Jesus Christ by demonstrating the love of God through improving lives and reducing human suffering.';
  const vision = ph?.vision || 'Demonstrate the love of God through acts of charity, creating sustainable communities where every person has access to education, healthcare, clean water, and nutritious food.';
  const donationRaised = ph?.donation_raised ?? 1000;
  const donationGoal = ph?.donation_goal ?? 15000;
  const progressPct = donationGoal > 0 ? Math.round((donationRaised / donationGoal) * 1000) / 10 : 0;

  useEffect(() => {
    document.title = 'PureHeart Africa Program — TGMCI NGO Initiative';
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }); },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleDonate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (honeypot.trim()) { setFormStatus('success'); setFormMsg('Thank you for your generosity!'); return; }
    if (!firstName || !lastName || !email) return;
    setFormStatus('loading');

    const fullName = `${firstName} ${lastName}`.trim();
    const { error: insertErr } = await supabase.from('contact_submissions').insert({
      name: fullName,
      email,
      subject: 'Donation Inquiry',
      message: `Donation amount: ${donationAmount || 'Not specified'}\nPayment method: ${paymentMethod || 'Not specified'}`,
    });

    if (insertErr) {
      setFormStatus('error');
      setFormMsg('Something went wrong. Please try again.');
      return;
    }

    // Record the donation for the admin dashboard and send confirmation emails.
    try {
      await recordDonation({
        full_name: fullName,
        email,
        amount: donationAmount || 'Not specified',
        payment_method: paymentMethod || 'Not specified',
      });
      await sendDonationEmails({
        donorEmail: email,
        fullName,
        amount: donationAmount || 'Not specified',
        paymentMethod: paymentMethod || 'Not specified',
      });
    } catch {
      // Donation is already recorded; email errors should not block the success message.
    }

    setFormStatus('success');
    setFormMsg('Thank you for your generous donation inquiry! We have notified our team, and the church admin will send you the payment details shortly so you can complete your gift securely.');
    setFirstName(''); setLastName(''); setEmail(''); setDonationAmount(''); setPaymentMethod('');
  };

  const AMOUNTS_CFA = ['1000 CFA', '2500 CFA', '5000 CFA', '10000 CFA'];
  const AMOUNTS_USD = ['$25', '$50', '$75', '$100'];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-end overflow-hidden pt-32 pb-16">
          <div className="absolute inset-0">
            <SlotImage
              slot="pureheart_hero"
              fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/dcf4943e-8e79-4023-9c15-6887cfd14a32_compressed_WhatsApp-Image-2026-09-10-at-8.19.32-AM.webp"
              alt="PureHeart Africa"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
          </div>
          <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8 w-full">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-3">PureHeart Africa</p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white leading-tight mb-4">
              PureHeart Africa<br />Saving Humanity
            </h1>
            <div className="w-16 h-1 bg-accent-500 mb-6" />
            <p className="text-white/80 text-lg leading-relaxed max-w-2xl mb-8">
              At PureHeart Africa, we are dedicated to uplifting communities across the African continent through sustainable development, education, and health initiatives. Our mission is to empower individuals and families, fostering a brighter future for all.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#donate" className="btn-accent">
                <i className="ri-heart-line" aria-hidden="true" />
                Donate Now
              </a>
              <a href="#programs" className="btn-outline-white">
                <i className="ri-information-line" aria-hidden="true" />
                Our Programs
              </a>
              <div className="flex gap-3 items-center">
                {['ri-facebook-fill', 'ri-twitter-x-fill', 'ri-linkedin-box-fill', 'ri-instagram-line'].map((icon) => (
                  <a key={icon} href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer" aria-label="Social media">
                    <i className={`${icon} text-sm`} aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 bg-background-100">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <AnimatedCounter target={s.value} prefix={s.prefix} suffix={s.suffix} />
                  <p className="text-foreground-500 font-label text-sm uppercase tracking-wide mt-2">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="reveal-left">
                <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Our Purpose</p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">Our Mission &amp; Vision</h2>
                <div className="w-12 h-1 bg-accent-500 mb-8" />

                <div className="space-y-6">
                  <div className="p-6 bg-background-100 rounded-xl border border-background-200">
                    <h3 className="font-heading font-bold text-xl text-foreground-800 mb-3">Our Mission</h3>
                    <p className="text-foreground-600 text-base leading-relaxed">
                      {mission}
                    </p>
                  </div>

                  <div className="p-6 bg-background-100 rounded-xl border border-background-200">
                    <h3 className="font-heading font-bold text-xl text-foreground-800 mb-3">Our Vision</h3>
                    <p className="text-foreground-600 text-base leading-relaxed">
                      {vision}
                    </p>
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-restaurant-line', label: 'Healthy Food' },
                    { icon: 'ri-hospital-line', label: 'Medical Care' },
                    { icon: 'ri-drop-line', label: 'Pure Water' },
                    { icon: 'ri-graduation-cap-line', label: 'Education' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 p-3 bg-primary-50 rounded-lg">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-600 text-white shrink-0">
                        <i className={`${item.icon} text-sm`} aria-hidden="true" />
                      </div>
                      <span className="text-sm font-label font-medium text-foreground-700">{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <Link to="/contact" className="btn-primary">Join Our Campaign</Link>
                </div>
              </div>

              {/* Slideshow of images */}
              <div className="reveal grid grid-cols-2 gap-4">
                {[
                  "https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/603ca32a-3de9-4ccf-97a7-9ef2481f8546_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-5.webp",
                  "https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/1ab415ec-9dc5-4ff9-83f5-083be47621da_compressed_WhatsApp-Image-2026-09-12-at-15.29.02-1.webp",
                  "https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/d4b76b2d-0b00-4684-9b37-6b17601f9688_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-1.webp",
                  "https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/3fe90688-4f52-4024-8d3f-b6f0e72c7cf7_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-6.webp",
                ].map((src, i) => (
                  <div key={i} className={`img-zoom rounded-xl overflow-hidden ${i === 0 || i === 2 ? 'h-52' : 'h-52 mt-6'}`}>
                    <SlotImage
                      slot={['pureheart_grid_1', 'pureheart_grid_2', 'pureheart_grid_3', 'pureheart_grid_4'][i]}
                      fallback={src}
                      alt={`PureHeart Africa program ${i + 1}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Donation Form */}
        <section id="donate" className="py-20 md:py-28 bg-background-100">
          <div className="max-w-[1000px] mx-auto px-4 md:px-8">
            <div className="text-center mb-12 reveal">
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Give Generously</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">Donate For Humanity</h2>
              <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
              <p className="text-foreground-500 mt-5 text-base max-w-lg mx-auto">
                Your donations help us implement our programs and reach more communities in need. Every contribution, no matter how small, makes a difference.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left — progress + info */}
              <div className="reveal-left">
                <div className="p-6 bg-background-50 rounded-xl border border-background-200 mb-6">
                  <h3 className="font-heading font-bold text-xl text-foreground-800 mb-2">Donate With a Pure Heart</h3>
                  <p className="text-foreground-500 text-sm mb-4">${donationRaised.toLocaleString()} of ${donationGoal.toLocaleString()} raised</p>
                  <div className="h-3 bg-background-200 rounded-full overflow-hidden">
                    <div className="h-full bg-accent-500 rounded-full" style={{ width: `${Math.min(progressPct, 100)}%` }} />
                  </div>
                  <p className="text-xs text-foreground-400 mt-2">{progressPct}% funded — keep giving!</p>
                </div>
                <SlotImage
                  slot="pureheart_giving"
                  fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/47fcfdce-9fca-4de5-be2f-48ee60c3afb0_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-8.webp"
                  alt="Giving hands — PureHeart donation"
                  className="w-full rounded-xl object-cover"
                />
              </div>

              {/* Right — form */}
              <div className="reveal">
                {formStatus === 'success' ? (
                  <div className="p-8 bg-green-50 border border-green-200 rounded-xl text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-4">
                      <i className="ri-check-line text-3xl" aria-hidden="true" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-green-800 mb-2">Thank You!</h3>
                    <p className="text-green-700 text-sm leading-relaxed">{formMsg}</p>
                    <button type="button" onClick={() => setFormStatus('idle')} className="btn-primary mt-6">
                      Make Another Donation
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleDonate} className="bg-background-50 rounded-xl border border-background-200 p-6 space-y-4">
                    {/* Honeypot */}
                    <div style={{ position: 'absolute', left: '-9999px' }}>
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                    </div>

                    <div>
                      <label className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-2">
                        Donation Amount (CFA)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AMOUNTS_CFA.map((amt) => (
                          <button
                            key={amt}
                            type="button"
                            onClick={() => setDonationAmount(amt)}
                            className={`px-4 py-2 rounded-lg text-sm font-label border cursor-pointer transition-colors ${donationAmount === amt ? 'bg-primary-600 text-white border-primary-600' : 'border-background-300 text-foreground-600 hover:border-primary-400'}`}
                          >
                            {amt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-2">
                        Donation Amount (USD)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AMOUNTS_USD.map((amt) => (
                          <button
                            key={amt}
                            type="button"
                            onClick={() => setDonationAmount(amt)}
                            className={`px-4 py-2 rounded-lg text-sm font-label border cursor-pointer transition-colors ${donationAmount === amt ? 'bg-primary-600 text-white border-primary-600' : 'border-background-300 text-foreground-600 hover:border-primary-400'}`}
                          >
                            {amt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <input
                        type="text"
                        name="donation_amount_custom"
                        placeholder="Custom Amount"
                        value={donationAmount}
                        onChange={(e) => setDonationAmount(e.target.value)}
                        className="w-full px-4 py-3 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-2">
                        Select Payment Method
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {['Offline Donation', 'PayPal', 'Mobile Money'].map((m) => (
                          <label key={m} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="payment_method"
                              value={m}
                              checked={paymentMethod === m}
                              onChange={(e) => setPaymentMethod(e.target.value)}
                              className="accent-primary-600"
                            />
                            <span className="text-sm text-foreground-600">{m}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-3">Personal Info</p>
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          name="first_name"
                          placeholder="First Name *"
                          required
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="px-4 py-3 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                        <input
                          type="text"
                          name="last_name"
                          placeholder="Last Name *"
                          required
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="px-4 py-3 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                        <input
                          type="email"
                          name="email"
                          placeholder="Email Address *"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="col-span-2 px-4 py-3 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                      </div>
                    </div>

                    {formStatus === 'error' && (
                      <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">{formMsg}</p>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="btn-accent w-full justify-center py-3.5"
                    >
                      {formStatus === 'loading' ? 'Processing...' : <><i className="ri-heart-line" aria-hidden="true" /> Donate</>}
                    </button>

                    <p className="text-xs text-foreground-400 text-center">
                      Your donation inquiry will be processed securely by our team. We will contact you with payment instructions.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Programs */}
        <section id="programs" className="py-20 md:py-28 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="text-center mb-12 reveal">
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">What We Do</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">Our Programs</h2>
              <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {programs.map((prog, idx) => (
                <div key={prog.title} className="reveal p-7 bg-background-100 rounded-xl border border-background-200" style={{ transitionDelay: `${idx * 100}ms` }}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-600 text-white shrink-0">
                      <i className={`${prog.icon} text-xl`} aria-hidden="true" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-foreground-800">{prog.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {prog.items.map((item) => (
                      <li key={item.bold} className="flex gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-accent-500 shrink-0 mt-0.5" aria-hidden="true" />
                        <span><strong className="text-foreground-700">{item.bold}</strong> {item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recent News */}
        <section className="py-16 bg-primary-800 text-white">
          <div className="max-w-[900px] mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-2xl font-bold mb-2">Recent News</h2>
              <p className="text-white/70 text-sm leading-relaxed max-w-lg">
                To know more about our PureHeart program, click to read all our recent news and see how you can save humanity with a pure heart.
              </p>
            </div>
            <Link to="/news" className="btn-accent shrink-0 whitespace-nowrap">Read All News</Link>
          </div>
        </section>

        {/* Official PureHeart Website */}
        <section className="py-20 bg-background-50">
          <div className="max-w-[1000px] mx-auto px-4 md:px-8 text-center">
            <div className="p-8 md:p-12 bg-background-100 rounded-2xl border-2 border-accent-300">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 mx-auto mb-6">
                <i className="ri-heart-3-fill text-3xl" aria-hidden="true" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                Visit Our Official PureHeart Website
              </h2>
              <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
                Explore the full mission, programs, and impact of PureHeart International — saving humanity with a pure heart across Africa and beyond.
              </p>
              <a
                href="https://pureheartsintl.org/"
                target="_blank"
                rel="noreferrer noopener"
                className="blink-cta inline-flex items-center gap-2 bg-accent-500 text-foreground-950 font-label font-bold uppercase tracking-wider text-sm md:text-base px-8 py-4 rounded-full"
              >
                <i className="ri-external-link-line" aria-hidden="true" />
                Visit pureheartsintl.org
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}